// frontend/src/hooks/useRoleMutations.ts — the roles write surface (create ·
// update · disable · reactivate). Each invalidates the `['roles', projectId]`
// PREFIX on success, so the list + any open detail/peek + the assignable picker
// (useProjectRoles) refetch together (FRONTEND-PATTERNS §Mutations + invalidation).
//
// Errors are NOT caught here — they bubble so the form shell / ConfirmDialog map
// the backend `code` (ROLE_NAME_EXISTS, ROLE_IN_USE) through errors.json. 401 is
// the apiClient's job (ADR-003). No invalidate(['me']): editing a role changes
// OTHER users' effective permissions (the backend clears its own permission cache,
// ADR-044/ADR-003) — not the acting admin's own /me (admins short-circuit).

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { CreateRoleRequest, UpdateRoleRequest } from '@ems-app/schemas/roles/requests';
import type { CreatedRoleResponse } from '@ems-app/schemas/roles/responses';
import { apiClient } from '../lib/apiClient.js';
import { rolesKeys } from './useRoles.js';
import { refreshOnSettled } from '../lib/settled.js';

function useInvalidateRoles(projectId: string): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: rolesKeys.all(projectId) });
}

/** POST …/roles — create a project role; returns its id. */
export function useCreateRole(
  projectId: string,
): UseMutationResult<CreatedRoleResponse, Error, CreateRoleRequest> {
  const invalidate = useInvalidateRoles(projectId);
  return useMutation({
    mutationFn: async (body) => (await apiClient.post<CreatedRoleResponse>(`/projects/${projectId}/roles`, body)).data,
    onSuccess: invalidate,
  });
}

/** PATCH …/roles/:id — partial update (scalars · permissions · managed_roles). */
export function useUpdateRole(
  projectId: string,
): UseMutationResult<void, Error, { roleId: number; body: UpdateRoleRequest }> {
  const invalidate = useInvalidateRoles(projectId);
  return useMutation({
    mutationFn: async ({ roleId, body }) => {
      await apiClient.patch(`/projects/${projectId}/roles/${roleId}`, body);
    },
    onSuccess: invalidate,
  });
}

/** POST …/roles/:id/{disable,reactivate} — lifecycle toggle. Disable may throw
 *  ROLE_IN_USE (the role is assigned to members) — the ConfirmDialog surfaces it. */
export function useSetRoleDisabled(
  projectId: string,
): UseMutationResult<void, Error, { roleId: number; disabled: boolean }> {
  const invalidate = useInvalidateRoles(projectId);
  return useMutation({
    mutationFn: async ({ roleId, disabled }) => {
      await apiClient.post(`/projects/${projectId}/roles/${roleId}/${disabled ? 'disable' : 'reactivate'}`);
    },
    onSettled: refreshOnSettled(invalidate),
  });
}
